// Theological frameworks and sacred wisdom data

export interface SacredText {
  id: string
  title: string
  tradition: string
  description: string
  keyTeachings: string[]
  symbol: string
}

export interface WorldFaith {
  id: string
  name: string
  symbol: string
  color: string
  founded: string
  followers: string
  coreBeliefs: string[]
  sacredTexts: string[]
  description: string
}

export interface UniversalTheme {
  id: string
  theme: string
  icon: string
  description: string
  traditions: {
    faith: string
    teaching: string
    source: string
  }[]
}

export interface SpiritualPractice {
  id: string
  practice: string
  description: string
  benefits: string[]
  traditions: string[]
}

export const sacredTexts: SacredText[] = [
  {
    id: 'bible',
    title: 'The Holy Bible',
    tradition: 'Christianity',
    description: 'The sacred scripture of Christianity, comprising the Old and New Testaments',
    keyTeachings: [
      'Love God with all your heart, soul, and mind',
      'Love your neighbor as yourself',
      'Forgiveness and redemption through grace',
      'The life and teachings of Jesus Christ'
    ],
    symbol: '✝'
  },
  {
    id: 'quran',
    title: 'The Holy Quran',
    tradition: 'Islam',
    description: 'The central religious text of Islam, believed to be the word of God revealed to Prophet Muhammad',
    keyTeachings: [
      'There is no god but Allah, and Muhammad is His messenger',
      'The Five Pillars: faith, prayer, charity, fasting, pilgrimage',
      'Mercy and compassion for all creation',
      'Justice and righteousness in all affairs'
    ],
    symbol: '☪'
  },
  {
    id: 'torah',
    title: 'The Torah',
    tradition: 'Judaism',
    description: 'The foundational text of Judaism, containing the five books of Moses',
    keyTeachings: [
      'The covenant between God and the Jewish people',
      'The Ten Commandments as ethical foundation',
      'Tikkun olam - repairing the world',
      'Justice, mercy, and walking humbly with God'
    ],
    symbol: '✡'
  },
  {
    id: 'bhagavad-gita',
    title: 'Bhagavad Gita',
    tradition: 'Hinduism',
    description: 'A 700-verse Hindu scripture that is part of the epic Mahabharata',
    keyTeachings: [
      'Selfless action without attachment to results',
      'The eternal nature of the soul (Atman)',
      'Multiple paths to spiritual realization',
      'Devotion, knowledge, and righteous action'
    ],
    symbol: '🕉'
  },
  {
    id: 'tripitaka',
    title: 'Tripitaka',
    tradition: 'Buddhism',
    description: 'The traditional term for Buddhist scriptures, the "three baskets" of teachings',
    keyTeachings: [
      'The Four Noble Truths about suffering',
      'The Eightfold Path to enlightenment',
      'Compassion for all sentient beings',
      'The impermanence of all phenomena'
    ],
    symbol: '☸'
  },
  {
    id: 'guru-granth-sahib',
    title: 'Guru Granth Sahib',
    tradition: 'Sikhism',
    description: 'The central religious scripture of Sikhism, considered the living Guru',
    keyTeachings: [
      'One God who is the same for all people',
      'Honest living and sharing with others',
      'Equality of all human beings',
      'Devotion through meditation on the divine name'
    ],
    symbol: '☬'
  }
]

export const worldFaiths: WorldFaith[] = [
  {
    id: 'christianity',
    name: 'Christianity',
    symbol: '✝',
    color: '#8B5CF6',
    founded: '~30 CE',
    followers: '2.4 billion',
    coreBeliefs: [
      'Belief in one God as Trinity',
      'Jesus Christ as Son of God and Savior',
      'Salvation through faith and grace',
      'Resurrection and eternal life'
    ],
    sacredTexts: ['Bible (Old & New Testament)'],
    description: 'Christianity centers on the life, teachings, death, and resurrection of Jesus Christ as the path to salvation and eternal life with God.'
  },
  {
    id: 'islam',
    name: 'Islam',
    symbol: '☪',
    color: '#10B981',
    founded: '610 CE',
    followers: '1.9 billion',
    coreBeliefs: [
      'Absolute monotheism (Tawhid)',
      'Muhammad as the final prophet',
      'The Quran as God\'s final revelation',
      'Day of Judgment and accountability'
    ],
    sacredTexts: ['Quran', 'Hadith'],
    description: 'Islam teaches submission to the will of Allah, following the Five Pillars, and living a life of righteousness, mercy, and devotion.'
  },
  {
    id: 'judaism',
    name: 'Judaism',
    symbol: '✡',
    color: '#3B82F6',
    founded: '~2000 BCE',
    followers: '14 million',
    coreBeliefs: [
      'One God who created the universe',
      'Covenant between God and Jewish people',
      'Torah as divine instruction',
      'Ethical monotheism and justice'
    ],
    sacredTexts: ['Torah', 'Talmud', 'Tanakh'],
    description: 'Judaism emphasizes the covenant relationship with God, ethical living according to the Torah, and the pursuit of justice and compassion.'
  },
  {
    id: 'hinduism',
    name: 'Hinduism',
    symbol: '🕉',
    color: '#F59E0B',
    founded: '~1500 BCE',
    followers: '1.2 billion',
    coreBeliefs: [
      'Brahman as ultimate reality',
      'Atman (eternal soul) in all beings',
      'Karma, dharma, and reincarnation',
      'Multiple paths to liberation (moksha)'
    ],
    sacredTexts: ['Vedas', 'Upanishads', 'Bhagavad Gita'],
    description: 'Hinduism encompasses diverse traditions centered on dharma, the cycle of rebirth, and the goal of spiritual liberation through various paths.'
  },
  {
    id: 'buddhism',
    name: 'Buddhism',
    symbol: '☸',
    color: '#EC4899',
    founded: '~500 BCE',
    followers: '500 million',
    coreBeliefs: [
      'The Four Noble Truths',
      'The Eightfold Path',
      'Impermanence and non-self',
      'Nirvana as liberation from suffering'
    ],
    sacredTexts: ['Tripitaka', 'Sutras', 'Commentaries'],
    description: 'Buddhism teaches the path to end suffering through wisdom, ethical conduct, and mental discipline, leading to enlightenment and nirvana.'
  },
  {
    id: 'sikhism',
    name: 'Sikhism',
    symbol: '☬',
    color: '#EF4444',
    founded: '1469 CE',
    followers: '30 million',
    coreBeliefs: [
      'One God (Ik Onkar)',
      'Equality of all humanity',
      'Service to others (Seva)',
      'Living honestly and sharing'
    ],
    sacredTexts: ['Guru Granth Sahib'],
    description: 'Sikhism emphasizes devotion to one God, equality of all people, honest living, and selfless service to the community.'
  }
]

export const universalThemes: UniversalTheme[] = [
  {
    id: 'love',
    theme: 'Divine Love',
    icon: '💝',
    description: 'Love as the highest spiritual principle, uniting humanity with the divine',
    traditions: [
      { faith: 'Christianity', teaching: 'God is love; whoever lives in love lives in God', source: '1 John 4:16' },
      { faith: 'Islam', teaching: 'Allah is full of loving-kindness to His servants', source: 'Quran 42:19' },
      { faith: 'Hinduism', teaching: 'The soul\'s journey is driven by divine love (Bhakti)', source: 'Narada Bhakti Sutra' },
      { faith: 'Buddhism', teaching: 'Loving-kindness (Metta) extends to all beings without limit', source: 'Metta Sutta' }
    ]
  },
  {
    id: 'unity',
    theme: 'Sacred Unity',
    icon: '🌐',
    description: 'The interconnectedness of all existence and the oneness of the divine',
    traditions: [
      { faith: 'Judaism', teaching: 'Hear, O Israel: The Lord our God, the Lord is One', source: 'Deuteronomy 6:4' },
      { faith: 'Islam', teaching: 'There is no deity except Allah', source: 'Quran 37:35' },
      { faith: 'Hinduism', teaching: 'Brahman alone is the one reality behind all', source: 'Chandogya Upanishad' },
      { faith: 'Sikhism', teaching: 'There is only One God whose name is Truth', source: 'Guru Granth Sahib' }
    ]
  },
  {
    id: 'compassion',
    theme: 'Compassion',
    icon: '🙏',
    description: 'Mercy, empathy, and care for the suffering of all beings',
    traditions: [
      { faith: 'Buddhism', teaching: 'Compassion is the wish for others to be free from suffering', source: 'Dalai Lama' },
      { faith: 'Christianity', teaching: 'Blessed are the merciful, for they will receive mercy', source: 'Matthew 5:7' },
      { faith: 'Islam', teaching: 'Show mercy to those on earth, and the One above will show mercy to you', source: 'Hadith' },
      { faith: 'Judaism', teaching: 'What is hateful to you, do not do to your neighbor', source: 'Talmud, Shabbat 31a' }
    ]
  },
  {
    id: 'transcendence',
    theme: 'Transcendence',
    icon: '✨',
    description: 'The journey beyond the material to encounter the infinite',
    traditions: [
      { faith: 'Hinduism', teaching: 'The Self is not born, nor does it die; it is eternal', source: 'Bhagavad Gita 2:20' },
      { faith: 'Buddhism', teaching: 'Nirvana is the highest happiness, beyond worldly pleasure', source: 'Dhammapada' },
      { faith: 'Christianity', teaching: 'We are citizens of heaven', source: 'Philippians 3:20' },
      { faith: 'Sufism', teaching: 'Die before you die, and find that there is no death', source: 'Rumi' }
    ]
  },
  {
    id: 'service',
    theme: 'Sacred Service',
    icon: '🤲',
    description: 'Serving others as a path to spiritual fulfillment',
    traditions: [
      { faith: 'Sikhism', teaching: 'Seva (selfless service) is the path to union with God', source: 'Guru Granth Sahib' },
      { faith: 'Christianity', teaching: 'Whatever you did for the least of these, you did for me', source: 'Matthew 25:40' },
      { faith: 'Judaism', teaching: 'Tikkun Olam - we are called to repair the world', source: 'Rabbinic teaching' },
      { faith: 'Islam', teaching: 'The best of people are those who bring most benefit to others', source: 'Hadith' }
    ]
  },
  {
    id: 'wisdom',
    theme: 'Divine Wisdom',
    icon: '📜',
    description: 'The pursuit of knowledge that leads to spiritual understanding',
    traditions: [
      { faith: 'Judaism', teaching: 'The beginning of wisdom is the fear of the Lord', source: 'Proverbs 9:10' },
      { faith: 'Buddhism', teaching: 'Wisdom is the path to liberation from suffering', source: 'Noble Eightfold Path' },
      { faith: 'Islam', teaching: 'Seek knowledge from the cradle to the grave', source: 'Hadith' },
      { faith: 'Hinduism', teaching: 'Jnana (knowledge) leads to liberation', source: 'Vedanta' }
    ]
  }
]

export const spiritualPractices: SpiritualPractice[] = [
  {
    id: 'meditation',
    practice: 'Meditation & Contemplation',
    description: 'Quieting the mind to connect with the divine presence',
    benefits: ['Inner peace', 'Spiritual awareness', 'Mental clarity', 'Divine connection'],
    traditions: ['Buddhism', 'Hinduism', 'Christianity (Contemplative)', 'Sufism']
  },
  {
    id: 'prayer',
    practice: 'Prayer',
    description: 'Direct communication with the divine through words, thoughts, and devotion',
    benefits: ['Divine connection', 'Gratitude', 'Guidance', 'Community'],
    traditions: ['Christianity', 'Islam', 'Judaism', 'Sikhism']
  },
  {
    id: 'scripture-study',
    practice: 'Scripture Study',
    description: 'Deep engagement with sacred texts for wisdom and guidance',
    benefits: ['Knowledge', 'Spiritual growth', 'Ethical guidance', 'Inspiration'],
    traditions: ['All major traditions']
  },
  {
    id: 'service',
    practice: 'Service to Others',
    description: 'Expressing spirituality through acts of kindness and helping those in need',
    benefits: ['Compassion', 'Community', 'Purpose', 'Spiritual fulfillment'],
    traditions: ['Sikhism', 'Christianity', 'Judaism', 'Islam', 'Buddhism']
  },
  {
    id: 'fasting',
    practice: 'Fasting & Abstinence',
    description: 'Disciplining the body to focus on spiritual matters',
    benefits: ['Self-discipline', 'Spiritual focus', 'Empathy', 'Purification'],
    traditions: ['Islam', 'Christianity', 'Judaism', 'Hinduism', 'Buddhism']
  },
  {
    id: 'pilgrimage',
    practice: 'Pilgrimage',
    description: 'Sacred journeys to holy sites for spiritual transformation',
    benefits: ['Deep devotion', 'Community', 'Transformation', 'Gratitude'],
    traditions: ['Islam (Hajj)', 'Christianity', 'Hinduism', 'Buddhism', 'Judaism']
  }
]

export const inspirationalQuotes = [
  {
    text: 'Love the Lord your God with all your heart and with all your soul and with all your mind.',
    source: 'Jesus Christ',
    tradition: 'Christianity',
    reference: 'Matthew 22:37'
  },
  {
    text: 'In the name of Allah, the Most Gracious, the Most Merciful.',
    source: 'The Quran',
    tradition: 'Islam',
    reference: 'Opening of Surah 1'
  },
  {
    text: 'The soul is neither born, nor does it ever die; it is eternal, unchanging, and ancient.',
    source: 'Bhagavad Gita',
    tradition: 'Hinduism',
    reference: 'Chapter 2, Verse 20'
  },
  {
    text: 'Peace comes from within. Do not seek it without.',
    source: 'The Buddha',
    tradition: 'Buddhism',
    reference: 'Traditional teaching'
  },
  {
    text: 'There is One God and His name is Truth.',
    source: 'Guru Nanak',
    tradition: 'Sikhism',
    reference: 'Japji Sahib'
  },
  {
    text: 'What is hateful to you, do not do to your neighbor. That is the whole Torah; the rest is commentary.',
    source: 'Rabbi Hillel',
    tradition: 'Judaism',
    reference: 'Talmud, Shabbat 31a'
  }
]

export const demoSteps = [
  {
    number: 1,
    title: 'Sacred Wisdom',
    subtitle: 'Divine teachings across traditions',
    description: 'Explore the profound wisdom preserved in sacred texts from around the world, each offering unique insights into the nature of existence, ethics, and our relationship with the divine.',
    icon: '📖'
  },
  {
    number: 2,
    title: 'World Faiths',
    subtitle: 'Christianity, Islam, Judaism, Eastern traditions',
    description: 'Journey through the major world religions, understanding their origins, core beliefs, and the sacred texts that have guided billions of souls across millennia.',
    icon: '🌍'
  },
  {
    number: 3,
    title: 'Common Themes',
    subtitle: 'Unity, love, transcendence',
    description: 'Discover the golden threads that weave through all traditions - the universal principles of love, compassion, unity, and the search for transcendence.',
    icon: '🔗'
  },
  {
    number: 4,
    title: 'Key Texts',
    subtitle: 'Scripture and sacred writings',
    description: 'Delve into the foundational scriptures that have shaped civilizations, from the Bible and Quran to the Vedas and Tripitaka.',
    icon: '📜'
  },
  {
    number: 5,
    title: 'Interactive Study',
    subtitle: 'Explore the teachings',
    description: 'Engage with an interactive visualization that maps the relationships between teachings, traditions, and universal themes.',
    icon: '🔍'
  },
  {
    number: 6,
    title: 'The Divine Journey',
    subtitle: 'Personal spiritual growth',
    description: 'Chart your own path of spiritual growth with practices, insights, and resources drawn from the wisdom of all traditions.',
    icon: '✨'
  }
]
